import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
    MdAttachMoney,
    MdBusiness,
    MdCheckCircle,
    MdEdit,
    MdErrorOutline,
    MdFactCheck,
    MdPerson,
    MdPsychology,
    MdSavings,
} from 'react-icons/md';
import { toast } from 'react-toastify';
import { useAuth } from '../../Context/AuthContext';
import { PermissionCodes, permissionHas, resolveUserPermissionCodes } from '../../Utils/Permissions';
import { APPRoutes } from '../../Utils/Route';
import { getEligibilityVerification } from '../../Services/Claim360Service';
import BackButton from '../../Components/Common/BackButton';
import ViewEmptyState from '../../Components/Common/ViewEmptyState';
import { MUTED, PANEL_STYLE, TITLE } from '../../Components/Claim360/StediResultParts';
import DataGrid from '../../Components/Table/DataGrid';
import SearchableDropdown from '../../Components/Subscription/SearchableDropdown';
import { FieldGrid, formatDateTime, Section, STATUS_STYLE, SummaryTile } from '../../Components/Claim360/StediViewParts';
import {
    flattenFields as flatten, fieldLabel as keyLabel, formatDate, formatMoney, humanize, MENTAL_HEALTH_CODES, resolutionLines, responsePlans,
} from '../../Utils/stediUtils';

// Benefit types shown first, with friendlier names; any other type Stedi returns follows alphabetically.
const BENEFIT_ORDER = ['statuses', 'coPayment', 'coInsurance', 'deductible', 'outOfPocket', 'limitations'];
const BENEFIT_LABELS = {
    statuses: 'Coverage Status',
    coPayment: 'Copay',
    coInsurance: 'Coinsurance',
    deductible: 'Deductible',
    outOfPocket: 'Out of Pocket',
    limitations: 'Limitations',
};

const statusTone = (status) => {
    if (/^active/i.test(status)) return 'success';
    if (/inactive|failed|rejected|not found/i.test(status)) return 'danger';
    return 'warning';
};

const messageText = (message) => (typeof message === 'string'
    ? message
    : message?.description || message?.message || message?.text || '');

// Value cell of one benefit entry: a status, an amount, a percent or a visit limit.
function entryValue(entry) {
    if (entry.status) return humanize(entry.status);
    if (entry.amount !== undefined && entry.amount !== null && entry.amount !== '') return formatMoney(entry.amount);
    if (entry.percent !== undefined && entry.percent !== null && entry.percent !== '') {
        return `${Number((Number(entry.percent) * 100).toFixed(2))}%`;
    }
    return '-';
}

function entryNotes(entry) {
    const limits = (entry.serviceLimits ?? []).map(({ delivery }) => {
        const quantity = delivery?.quantity;
        const period = delivery?.period;
        if (!quantity) return '';
        return `${quantity.value} ${humanize(quantity.qualifier ?? '').toLowerCase()}${period ? ` per ${period.value} ${humanize(period.qualifier ?? '').toLowerCase()}` : ''}`;
    });
    return [
        entry.planCoverageDescription,
        ...limits,
        ...(entry.messages ?? []).map(messageText),
    ].filter(Boolean).join(' · ');
}

const isMentalHealth = (entry) => MENTAL_HEALTH_CODES.includes(entry?.service?.value);

// Inpatient / outpatient is not a field of its own in a 271: it is read from the entry's places of service,
// then its service type code, then the payer's wording. Entries with no such signal (e.g. MH, 30) apply to both.
const INPATIENT = 'INPATIENT';
const OUTPATIENT = 'OUTPATIENT';
const SETTING_LABELS = { [INPATIENT]: 'Inpatient', [OUTPATIENT]: 'Outpatient' };
const SETTING_OPTIONS = [[OUTPATIENT, 'Outpatient'], [INPATIENT, 'Inpatient'], ['ALL', 'All settings']]
    .map(([value, label]) => ({ value, label }));
// CMS place of service codes.
const INPATIENT_PLACES = ['21', '31', '51', '55', '56', '61'];
const OUTPATIENT_PLACES = ['02', '10', '11', '12', '19', '20', '22', '23', '24', '49', '50', '52', '53', '57', '58', '62', '71', '72', '81'];
// Payer wording that names the setting, e.g. "Room and Board Mental Nervous" or "Partial Hospitalization".
const INPATIENT_TEXT = /\binpatient\b|room and board|residential/i;
const OUTPATIENT_TEXT = /\boutpatient\b|partial hospitali|office visit|telehealth|telemedicine/i;
// X12 service type codes.
// 48/49 hospital inpatient / room and board, A5/A7 psychiatric room and board / inpatient, CE mental health
// provider inpatient, CG mental health facility inpatient, CI substance abuse facility inpatient,
// RT residential psychiatric treatment.
const INPATIENT_SERVICES = ['48', '49', 'A5', 'A7', 'CE', 'CG', 'CI', 'RT'];
// 50 hospital outpatient, 98 office visit, A8 psychiatric outpatient, UC urgent care, BB psychiatric partial
// hospitalization, BC psychiatric day care, CF mental health provider outpatient, CH mental health facility
// outpatient, CJ substance abuse facility outpatient.
const OUTPATIENT_SERVICES = ['50', '98', 'A8', 'UC', 'BB', 'BC', 'CF', 'CH', 'CJ'];

const settingOf = (inpatient, outpatient) => {
    if (inpatient === outpatient) return null;
    return inpatient ? INPATIENT : OUTPATIENT;
};

function entrySetting(entry) {
    const places = (entry.placesOfService ?? []).map((place) => String(place?.code ?? '').padStart(2, '0'));
    if (places.length) {
        const fromPlaces = settingOf(
            places.some((code) => INPATIENT_PLACES.includes(code)),
            places.some((code) => OUTPATIENT_PLACES.includes(code)),
        );
        if (fromPlaces) return fromPlaces;
    }
    const code = entry.service?.value;
    if (INPATIENT_SERVICES.includes(code)) return INPATIENT;
    if (OUTPATIENT_SERVICES.includes(code)) return OUTPATIENT;
    // The service's own name (e.g. "Mental Health Provider - Outpatient") before the payer's free-text notes,
    // which can list unrelated codes and wording.
    const fromName = settingOf(INPATIENT_TEXT.test(entry.service?.definition ?? ''), OUTPATIENT_TEXT.test(entry.service?.definition ?? ''));
    if (fromName) return fromName;
    const text =[entry.planCoverageDescription, ...(entry.messages ?? []).map(messageText)].join(' ');
    return settingOf(INPATIENT_TEXT.test(text), OUTPATIENT_TEXT.test(text));
}

const matchesSetting = (entry, setting) => {
    if (setting === 'ALL') return true;
    const own = entrySetting(entry);
    return !own || own === setting;
};

// Statuses take their coverage colour; amounts and percents a neutral chip.
const valueStyle = (entry) => (entry.status
    ? STATUS_STYLE[statusTone(humanize(entry.status))]
    : { backgroundColor: 'var(--hc-tint-2)', color: 'var(--hc-text-title)' });

// Full-width sections lay their fields out in a row: up to four across.
const WIDE_FIELD_COL = 'col-12 col-sm-6 col-lg-4 col-xl-3';

const serviceText = (entry) => entry.service?.definition || entry.service?.value || 'All services';

// Service and value always show; the optional columns only when some entry has a value for them.
const BENEFIT_COLUMNS = [
    {
        id: 'service',
        label: 'Service',
        minWidth: 200,
        accessor: serviceText,
        format: (_value, entry) => (
            <>
                <div className="fw-semibold" style={TITLE}>{serviceText(entry)}</div>
                {entry.service?.value && <span className="small" style={MUTED}>{entry.service.system} · {entry.service.value}</span>}
            </>
        ),
    },
    {
        id: 'value',
        label: 'Value',
        minWidth: 130,
        accessor: entryValue,
        format: (_value, entry) => (
            <span className="badge rounded-pill fw-bold px-3 py-2" style={{ fontSize: '0.82rem', ...valueStyle(entry) }}>
                {entryValue(entry)}
            </span>
        ),
    },
    { id: 'setting', label: 'Setting', minWidth: 120, accessor: (entry) => SETTING_LABELS[entrySetting(entry)] },
    { id: 'network', label: 'Network', minWidth: 140, accessor: (entry) => entry.network?.indicator && humanize(entry.network.indicator) },
    { id: 'coverageLevel', label: 'Coverage Level', minWidth: 140, accessor: (entry) => entry.coverageLevel && humanize(entry.coverageLevel) },
    { id: 'timePeriod', label: 'Time Period', minWidth: 130, accessor: (entry) => entry.timePeriod && humanize(entry.timePeriod) },
    { id: 'insuranceType', label: 'Insurance Type', minWidth: 160, accessor: (entry) => entry.insuranceType && humanize(entry.insuranceType) },
    { id: 'notes', label: 'Notes', minWidth: 260, accessor: entryNotes },
].map((column) => (column.format ? column : { ...column, format: (_value, entry) => column.accessor(entry) || '-' }));

function BenefitTable({ entries }) {
    const columns = BENEFIT_COLUMNS.filter((column) => column.id === 'service' || column.id === 'value'
        || entries.some((entry) => column.accessor(entry)));
    return <DataGrid columns={columns} rows={entries} defaultRowsPerPage={10} getRowId={(_entry, index) => index} />;
}

function PlanBenefits({ plan, index, mentalHealthOnly, setting }) {
    const benefits = plan.benefits ?? {};
    const types = Object.keys(benefits)
        .filter((key) => key !== 'invalidEntries' && Array.isArray(benefits[key]))
        .sort((a, b) => {
            const rank = (key) => (BENEFIT_ORDER.includes(key) ? BENEFIT_ORDER.indexOf(key) : BENEFIT_ORDER.length);
            return rank(a) - rank(b) || a.localeCompare(b);
        });
    const groups = types
        .map((type) => [type, benefits[type].filter((entry) => (!mentalHealthOnly || isMentalHealth(entry))
            && matchesSetting(entry, setting))])
        .filter(([, entries]) => entries.length);
    const planFields = flatten(Object.fromEntries(Object.entries(plan).filter(([key]) => key !== 'benefits' && key !== 'name')));

    return (
        <div className="d-flex flex-column gap-3">
            <div className="d-flex flex-wrap align-items-center gap-2">
                <span className="fw-bold fs-6" style={TITLE}>{plan.name || `Plan ${index + 1}`}</span>
                {planFields.map(([label, value]) => (
                    <span key={label} className="badge rounded-pill border fw-semibold px-3 py-2" style={{ ...PANEL_STYLE, color: 'var(--hc-text-title)' }}>
                        {label}: {value}
                    </span>
                ))}
            </div>
            {groups.length === 0 ? (
                <div className="small" style={MUTED}>
                    {[
                        'No',
                        setting !== 'ALL' && SETTING_LABELS[setting].toLowerCase(),
                        mentalHealthOnly ? 'mental health (MH)' : 'benefit',
                        'entries in this plan.',
                    ].filter(Boolean).join(' ')}
                </div>
            ) : groups.map(([type, entries]) => (
                <div key={type}>
                    <div className="small fw-bold text-uppercase mb-2" style={{ ...MUTED, letterSpacing: '0.06em' }}>
                        {BENEFIT_LABELS[type] ?? keyLabel(type)} <span className="fw-normal">({entries.length})</span>
                    </div>
                    <BenefitTable entries={entries} />
                </div>
            ))}
        </div>
    );
}

export function EligibilityVerificationView() {
    const navigate = useNavigate();
    const { id } = useParams();
    const { token, permissions, decodedToken } = useAuth();
    const userPermissionCodes = React.useMemo(
        () => resolveUserPermissionCodes({ permissions, decodedToken }),
        [permissions, decodedToken],
    );
    const canEdit = permissionHas(userPermissionCodes, PermissionCodes.ELIGIBILITY_VERIFICATION_EDIT, decodedToken);
    const canViewList = permissionHas(userPermissionCodes, PermissionCodes.ELIGIBILITY_VERIFICATION_LIST, decodedToken);
    const [eligibility, setEligibility] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [mentalHealthOnly, setMentalHealthOnly] = React.useState(true);
    const [setting, setSetting] = React.useState(OUTPATIENT);

    React.useEffect(() => {
        if (!token) return undefined;
        let cancelled = false;
        setLoading(true);
        getEligibilityVerification(id, token)
            .then((response) => {
                if (!cancelled) setEligibility(response?.data ?? null);
            })
            .catch((error) => {
                console.error('Failed to load eligibility verification:', error);
                if (!cancelled) {
                    setEligibility(null);
                    toast.error(error.message || 'Failed to load eligibility verification.');
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [id, token]);

    if (loading) {
        return (
            <div className="spinner position-absolute d-flex align-items-center justify-content-center">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (!eligibility) {
        return (
            <ViewEmptyState
                title="Eligibility Verification Not Found"
                description="This eligibility verification record is no longer available."
                icon={MdCheckCircle}
                canViewList={canViewList}
                backRoute={APPRoutes.ELIGIBILITY_VERIFICATION}
                backLabel="Back to Eligibility Verifications"
            />
        );
    }

    const response = eligibility.response ?? {};
    const errors = Array.isArray(response.errors) ? response.errors : [];
    const warnings = Array.isArray(response.warnings) ? response.warnings : [];
    // Real-time checks answer in plans[]; batch checks in Stedi's original benefitsInformation[] format.
    const plans = responsePlans(response);
    const status = humanize(eligibility.status
        || (eligibility.payerRejected ? (errors[0]?.description || 'Payer Rejected') : null)
        || eligibility.state
        || 'Pending');
    const payerName = eligibility.payerName
        || (typeof response.payer?.name === 'string' ? response.payer.name : response.payer?.name?.organization);
    const payerId = eligibility.payerId || response.payerId;

    const subscriberFields = [
        ['Name', eligibility.patientName],
        ['Date of Birth', formatDate(eligibility.dateOfBirth)],
        ['Member ID', eligibility.memberId],
        // Whatever else the payer returned about the subscriber (gender, address, group, plan dates...).
        ...flatten(response.subscriber).filter(([label]) => !['First Name', 'Last Name', 'Member ID', 'Date Of Birth'].includes(label)),
    ];
    const dependents = Array.isArray(response.dependents) ? response.dependents : [];
    const checkFields = [
        ['Organization', eligibility.organizationName],
        ['Payer', payerName && `${payerName}${payerId ? ` (${payerId})` : ''}`],
        ['Source', eligibility.source && humanize(eligibility.source)],
        ['Status', eligibility.state && humanize(eligibility.state)],
        ['Eligibility Result', eligibility.eligibilityResult && humanize(eligibility.eligibilityResult)],
        ['Checked By', eligibility.createdByName],
        ['Checked On', formatDateTime(eligibility.createdOn)],
    ].filter(([, value]) => value && value !== '-');
    const providerName = response.provider?.name?.organization
        || [response.provider?.name?.person?.firstName, response.provider?.name?.person?.lastName].filter(Boolean).join(' ')
        || response.provider?.providerName;
    const providerNpi = response.provider?.npi;

    return (
        <div className="container-fluid py-4">
            <div className="d-flex flex-wrap gap-2 align-items-center justify-content-between mb-3">
                {canViewList && (
                    <BackButton label="Back to Eligibility Verifications" onClick={() => navigate(APPRoutes.ELIGIBILITY_VERIFICATION)} />
                )}
                {canEdit && (
                    <button
                        type="button"
                        className="btn hc-bg-primary text-white rounded-3 fw-semibold d-inline-flex align-items-center gap-2"
                        onClick={() => navigate(APPRoutes.ELIGIBILITY_VERIFICATION_EDIT.replace(':id', id), { state: { eligibility } })}
                    >
                        <MdEdit size={18} /> Re-check Eligibility
                    </button>
                )}
            </div>

            <div className="p-4 p-lg-5 mb-4 overflow-hidden hc-hero-banner position-relative hc-hero-banner-primary">
                <div aria-hidden="true" className="hc-hero-circle-tr position-absolute rounded-circle pe-none" />
                <div aria-hidden="true" className="hc-hero-circle-bl position-absolute rounded-circle pe-none" />
                <div className="row g-4 align-items-center position-relative">
                    <div className="col-lg-8">
                        <span
                            className="px-3 py-2 rounded-pill fw-semibold d-inline-block mb-3"
                            style={{ fontSize: '0.82rem', ...STATUS_STYLE[statusTone(status)] }}
                        >
                            MH Coverage: {status}
                        </span>
                        <h2 className="fw-bold text-white mb-2" style={{ fontSize: 'clamp(1.45rem, 2.2vw, 2rem)' }}>
                            {eligibility.patientName || '-'}
                        </h2>
                        <p className="mb-1" style={{ color: 'rgba(var(--hc-white-rgb), 0.78)' }}>
                            Member ID: {eligibility.memberId || '-'} · DOB: {formatDate(eligibility.dateOfBirth)}
                        </p>
                        <p className="mb-0" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                            Payer: {payerName || '-'}{payerId ? ` (${payerId})` : ''}
                        </p>
                    </div>
                    <div className="col-lg-4">
                        <div className="p-3 rounded-4" style={{ backgroundColor: 'rgba(var(--hc-white-rgb), 0.10)' }}>
                            <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Organization</p>
                            <p className="fw-bold text-white mb-3 fs-5">{eligibility.organizationName || '-'}</p>
                            {(providerName || providerNpi) && (
                                <>
                                    <p className="mb-1 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>Provider</p>
                                    <p className="fw-semibold text-white mb-3">
                                        {providerName || '-'}{providerNpi ? ` · NPI ${providerNpi}` : ''}
                                    </p>
                                </>
                            )}
                            <p className="mb-0 small" style={{ color: 'rgba(var(--hc-white-rgb), 0.65)' }}>
                                Checked {formatDateTime(eligibility.createdOn)}
                            </p>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row g-3 mb-4">
                <SummaryTile icon={MdPsychology} label="MH Coverage" value={eligibility.status && humanize(eligibility.status)} hint="Service type MH" />
                <SummaryTile icon={MdAttachMoney} label="MH Copay" value={eligibility.copay != null && formatMoney(eligibility.copay)} hint="In-network, individual" />
                <SummaryTile icon={MdSavings} label="MH Deductible Remaining" value={eligibility.deductible != null && formatMoney(eligibility.deductible)} hint="In-network, individual" />
                <SummaryTile icon={MdFactCheck} label="Plans Returned" value={String(plans.length)} hint={`${errors.length} error${errors.length === 1 ? '' : 's'} · ${warnings.length} warning${warnings.length === 1 ? '' : 's'}`} />
            </div>

            {(errors.length > 0 || warnings.length > 0) && (
                <Section icon={MdErrorOutline} title="Payer Messages" className="mb-4">
                    <div className="d-flex flex-column gap-3">
                        {[...errors.map((item) => ['error', item]), ...warnings.map((item) => ['warning', item])].map(([kind, item], index) => {
                            const lines = resolutionLines(item.possibleResolutions);
                            return (
                                <div key={index} className="rounded-4 border p-3" style={PANEL_STYLE}>
                                    <div className="d-flex flex-wrap align-items-center gap-2 mb-1">
                                        <span className="badge rounded-pill px-3 py-2" style={STATUS_STYLE[kind === 'error' ? 'warning' : 'success']}>
                                            {humanize(kind)}{item.code ? ` · Code ${item.code}` : ''}
                                        </span>
                                        <span className="fw-bold" style={TITLE}>{item.description || messageText(item) || 'Payer message'}</span>
                                    </div>
                                    {item.followupAction && <div className="small mb-2" style={MUTED}>{item.followupAction}</div>}
                                    {lines.length > 0 && (
                                        <ul className="small mb-0 ps-3 d-flex flex-column gap-1" style={{ color: 'var(--hc-body-text)' }}>
                                            {lines.map((line) => <li key={line}>{line}</li>)}
                                        </ul>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </Section>
            )}

            <Section icon={MdPerson} title="Subscriber" className="mb-4">
                <FieldGrid fields={subscriberFields} colClass={WIDE_FIELD_COL} />
                {dependents.map((dependent, index) => (
                    <div key={index} className="mt-4">
                        <div className="small fw-bold text-uppercase mb-2" style={{ ...MUTED, letterSpacing: '0.06em' }}>Dependent {index + 1}</div>
                        <FieldGrid fields={flatten(dependent)} colClass={WIDE_FIELD_COL} />
                    </div>
                ))}
            </Section>

            {plans.length > 0 && (
                <Section
                    icon={MdFactCheck}
                    title="Plan Benefits"
                    className="mb-4"
                    aside={(
                        <div className="d-flex flex-wrap align-items-center gap-2">
                            {/* Inpatient / outpatient is read from each entry's place of service, service type or payer
                                notes; entries with neither apply to both. */}
                            <div style={{ minWidth: 180 }}>
                                <SearchableDropdown
                                    placeholder="Care setting"
                                    options={SETTING_OPTIONS}
                                    value={setting}
                                    onChange={(value) => setSetting(value || OUTPATIENT)}
                                />
                            </div>
                            <div className="d-flex gap-1 p-1 rounded-pill border" role="tablist" style={PANEL_STYLE}>
                                {[[true, 'Mental health (MH)'], [false, 'All services']].map(([value, label]) => (
                                    <button
                                        key={label}
                                        type="button"
                                        role="tab"
                                        aria-selected={mentalHealthOnly === value}
                                        className={`btn btn-sm rounded-pill fw-semibold px-3 border-0 ${mentalHealthOnly === value ? 'hc-bg-primary text-white' : 'text-secondary'}`}
                                        onClick={() => setMentalHealthOnly(value)}
                                    >
                                        {label}
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}
                >
                    <div className="d-flex flex-column gap-4">
                        {plans.map((plan, index) => (
                            <PlanBenefits key={index} plan={plan} index={index} mentalHealthOnly={mentalHealthOnly} setting={setting} />
                        ))}
                    </div>
                </Section>
            )}

            <Section icon={MdBusiness} title="Check" className="mb-4">
                <FieldGrid fields={checkFields} colClass={WIDE_FIELD_COL} />
            </Section>

        </div>
    );
}

export default EligibilityVerificationView;
